import CantileverCard from 'components/CantileverCard'
import { ImagesProvider, ImagesRow } from 'features/assetDatabase'
import { useRolodexFileLSBulk, useRolodexSignedURLs } from 'features/rolodex'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoFileCategory, GeckoReport } from 'types'

const NoImagesContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2.875rem;
  align-self: stretch;
  color: var(--text-color);
`

const LoadingContainer = styled(NoImagesContainer)`
  // Inherits all styles from NoImagesContainer
`

interface WalkdownImagesSectionProps {
  reports: GeckoReport[]
  isLoading: boolean
  refreshReports: () => Promise<void>
}

const WalkdownImagesSection: React.FC<WalkdownImagesSectionProps> = ({
  reports,
  isLoading,
  refreshReports,
}) => {
  const [primaryImageIndex, setPrimaryImageIndex] = useState<number | null>(
    null
  )
  const [imageVisibility, setImageVisibility] = useState<boolean[]>([])

  // Initialize image visibility when reports change
  React.useEffect(() => {
    setImageVisibility(new Array(reports.length).fill(true))
  }, [reports.length])

  const imageReports = useMemo(() => {
    return reports.filter(
      (report) => report.data.file_category === GeckoFileCategory.IMAGE
    )
  }, [reports])

  const imageIds = useMemo(() => {
    return imageReports.map((report) => report.id)
  }, [imageReports])

  const fileQueries = useMemo(() => {
    return imageIds.map((id) => ({ entry_id: id }))
  }, [imageIds])

  const { fileRefs, isLoading: isLoadingFileRefs } =
    useRolodexFileLSBulk(fileQueries)

  // Filter in thumbnail files
  const filteredFileRefs = useMemo(() => {
    return fileRefs.filter((ref) => ref.path.startsWith('thumbnail_'))
  }, [fileRefs])

  const {
    urls: imageUrls,
    isLoading: isLoadingImageUrls,
    forceRefresh: refreshImageUrls,
  } = useRolodexSignedURLs(filteredFileRefs)

  const forceRefresh = useCallback(async () => {
    await Promise.all([refreshReports(), refreshImageUrls()])
  }, [refreshReports, refreshImageUrls])

  const savePrimaryImage = useCallback(async (index: number): Promise<void> => {
    setPrimaryImageIndex(index)
    // TODO: Implement saving primary image if needed
  }, [])

  const isLoadingData = isLoading || isLoadingFileRefs || isLoadingImageUrls

  if (isLoadingData) {
    return (
      <CantileverCard>
        <CantileverCard.CardHeader leadingContent='Images' />
        <CantileverCard.Section topPadding>
          <CantileverCard.Group>
            <LoadingContainer>Loading images...</LoadingContainer>
          </CantileverCard.Group>
        </CantileverCard.Section>
      </CantileverCard>
    )
  }

  if (imageReports.length === 0) {
    return (
      <CantileverCard>
        <CantileverCard.CardHeader leadingContent='Images' />
        <CantileverCard.Section topPadding>
          <CantileverCard.Group>
            <NoImagesContainer>No images</NoImagesContainer>
          </CantileverCard.Group>
        </CantileverCard.Section>
      </CantileverCard>
    )
  }

  return (
    <ImagesProvider
      assetThumbnailURLs={imageUrls}
      assetThumbnails={reports}
      imageVisibility={imageVisibility}
      setImageVisibility={setImageVisibility}
      forceRefresh={forceRefresh}
      components={[]}
      inspections={[]}
      canUploadFiles={true}
      unit={undefined}>
      <CantileverCard>
        <CantileverCard.CardHeader leadingContent={'Images'} />
        <CantileverCard.Section topPadding>
          <CantileverCard.Group>
            {isLoading || isLoadingImageUrls ? (
              <LoadingContainer>Loading images...</LoadingContainer>
            ) : imageUrls.length > 0 ? (
              <ImagesRow
                onlyShowImageOnPopup={true}
                primaryImageIndex={primaryImageIndex}
                savePrimaryImage={savePrimaryImage}
                subSectionURLs={imageUrls}
                subSectionThumbnails={reports}
              />
            ) : (
              <NoImagesContainer>No Images</NoImagesContainer>
            )}
          </CantileverCard.Group>
        </CantileverCard.Section>
      </CantileverCard>
    </ImagesProvider>
  )
}

export default WalkdownImagesSection
